import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import VodPlayer, { sourceFor } from "./VodPlayer";
import "./ClippingScanner.css";

/**
 * ClippingScanner — Sherlock "Visual Analysis" style live tracker. Stays on
 * screen for the whole clipping job: the active brand image sits in a scanning
 * circle (dashed ring + pulse + sweep beam) while real progress + a results
 * count tick up. It only ends when the job finishes (all clips spat out), then
 * the parent hands off to the swipe viewer.
 *
 * Props:
 *   brandImage   string?  the active brand's image (falls back to the mascot)
 *   title        string?  video / channel title
 *   progress     number   0-100 (real job progress)
 *   resultsCount number   clips produced so far
 *   done         bool     job finished (all clips ready)
 *   failed       bool     job failed
 *   onCancel     ()=>void cancel the clipping job
 *   onClose      ()=>void hide the tracker (job keeps running in the background)
 *   inFlow       part of onboarding (the free "try it" run): no way out until the
 *                clips are done, and no early peek at partial clips
 */
const STAGES = [
  "Scanning the footage",
  "Finding the best moments",
  "Cutting your clips",
  "Writing captions",
  "Polishing the edits",
];
// Live (auto-clip) stages — the stream is ongoing, not a fixed job. The last
// stage names the creator's actual platform (never hardcode Rumble).
const PLATFORM_LABEL = { twitch: "Twitch", youtube: "YouTube", kick: "Kick", rumble: "Rumble", x: "X", ganjing: "Ganjing World" };
function liveStages(platform) {
  const label = PLATFORM_LABEL[String(platform || "").toLowerCase()] || "your channel";
  return [
    "Watching your stream",
    "Catching the best moments",
    "Cutting clips as they happen",
    `Branding for ${label}`,
  ];
}


// The 4 steps of a pasted-link job, from its real progress.
const STEPS = ["Queuing", "Analyzing", "Cutting", "Finishing"];
function stepIndex(progress, status, done) {
  if (done) return 4;
  if (status === "queued" || status === "pending" || progress < 5) return 0;
  if (progress < 55) return 1;
  if (progress < 90) return 2;
  return 3;
}

// "~4 min left", counting down between the worker's estimate updates.
function useMinutesLeft(etaSeconds) {
  const [anchor, setAnchor] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (typeof etaSeconds === "number" && etaSeconds >= 0) setAnchor({ eta: etaSeconds, at: Date.now() });
  }, [etaSeconds]);
  useEffect(() => {
    if (!anchor) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [anchor]);
  if (!anchor) return "Estimating time left…"; // the worker sends its estimate a little way in
  const left = Math.max(0, anchor.eta - (now - anchor.at) / 1000);
  return left < 60 ? "Less than a minute left" : `~${Math.ceil(left / 60)} min left`;
}

export default function ClippingScanner({ brandImage, title, progress = 0, resultsCount = 0, done = false, failed = false, live = false, ended = false, endedReason, platform, videoUrl, etaSeconds, status, currentStep, onCancel, onClose, onViewClips, onUpgrade, inFlow = false, onRetry }) {
  const [stage, setStage] = useState(0);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [brandImage]); // a new picture gets a fresh try
  const stages = live ? liveStages(platform) : STAGES;
  const liveEnded = live && ended;          // auto-clip session finished
  const outOfCredits = liveEnded && endedReason === "credits";

  useEffect(() => {
    if (done || failed || ended) return;
    const t = setInterval(() => setStage((s) => (s + 1) % stages.length), 1500);
    return () => clearInterval(t);
  }, [done, failed, ended, stages.length]);

  // Cancelling kills the job, so it's a small link that asks first — the big
  // button just closes the screen and lets the job keep running. (A new
  // subscriber tapped the old full-width "Cancel clipping" to leave the screen
  // and lost their first job.)
  const confirmCancel = (msg) => {
    if (typeof window === "undefined" || window.confirm(msg)) onCancel?.();
  };

  const src = !imgFailed && brandImage && /^(https?:|data:|blob:)/.test(brandImage) ? brandImage : "/mascot.png";
  const label = failed ? "SOMETHING WENT WRONG"
    : outOfCredits ? "OUT OF CREDITS"
    : liveEnded ? "CLIPS READY"
    : live ? "LIVE"
    : done ? "CLIPS READY"
    : progress < 15 ? "PREPARING" : progress < 60 ? "ANALYZING" : progress < 92 ? "RENDERING" : "FINISHING";
  const sub = failed ? "This one didn't work out. Try again."
    : outOfCredits ? "You're out of credits. Top up and clipping picks right back up if you're still live."
    : liveEnded ? "Your stream ended — clips are ready."
    : done ? "Opening your clips…"
    // The worker's real step ("Fetching video", "Rendering clip 2/5") beats a
    // rotating guess.
    : !live && currentStep ? `${String(currentStep).replace(/\.+$/, "")}…`
    : `${stages[stage]}…`;
  // When done, the bar is 100% regardless of the last progress value the job
  // reported — "CLIPS READY" and a 95% bar would look broken.
  const pct = done ? 100 : Math.max(0, Math.min(100, Math.round(progress)));
  const source = !live ? sourceFor(videoUrl) : null;
  const embed = !!source;
  const [playerFailed, setPlayerFailed] = useState(false);
  const minutesLeft = useMinutesLeft(!live && !done && !failed ? etaSeconds : null);
  const step = stepIndex(progress, status, done);

  // A "Keep clipping in background" / "Close" button already leaves the screen,
  // so the top-left X only shows when there's no other way out.
  const hasExit = liveEnded
    ? !(outOfCredits && onUpgrade)
    : live || (!done && !inFlow && !(resultsCount > 0 && !failed));

  // Rendered on <body> so it sits above everything (the bottom nav was
  // showing through when it lived inside the page).
  return createPortal(
    <div className="clsc-overlay">
      <div className="clsc-head">
        {inFlow || hasExit ? <span className="clsc-x clsc-x--blank" aria-hidden="true" /> : (
          <button className="clsc-x" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></svg>
          </button>
        )}
        {/* Brand, not a status word: the status is already shown below, and
            screenshots / screen recordings of this screen carry the name. */}
        <span className="clsc-title-top clsc-brand" aria-label="Clipbait">
          <span className="clsc-brand-row">
            <img className="clsc-brand-logo" src="/mascot.png" alt="" />
            <span className="clsc-brand-mark">CLIPBAIT<span className="clsc-brand-tld">.AI</span></span>
          </span>
        </span>
      </div>

      <div className="clsc-body">
        {embed && !playerFailed ? (
          <div className={`clsc-player${done ? " clsc-player--done" : ""}`}>
            <VodPlayer source={source} onFail={() => setPlayerFailed(true)} />
            {!done && !failed && <span className="clsc-player-scan" aria-hidden="true" />}
            {!done && !failed && <span className="clsc-player-grid" aria-hidden="true" />}
            <span className="clsc-corner clsc-corner--tl" aria-hidden="true" />
            <span className="clsc-corner clsc-corner--tr" aria-hidden="true" />
            <span className="clsc-corner clsc-corner--bl" aria-hidden="true" />
            <span className="clsc-corner clsc-corner--br" aria-hidden="true" />
          </div>
        ) : (
        <div className={`clsc-ring${(done || liveEnded) ? " clsc-ring--done" : ""}${(live && !ended) ? " clsc-ring--live" : ""}`}>
          <span className="clsc-dash" />
          <span className="clsc-pulse" />
          <div className="clsc-frame">
            <img className="clsc-img" src={src} alt="" draggable={false} onError={() => setImgFailed(true)} />
            {!done && !failed && !ended && <div className="clsc-beam" />}
          </div>
        </div>
        )}
        {embed && !playerFailed && (
          <div className={`clsc-ring clsc-ring--mini${done ? " clsc-ring--done" : ""}`} aria-hidden="true">
            <span className="clsc-dash" />
            <span className="clsc-pulse" />
            <div className="clsc-frame">
              <img className="clsc-img" src={src} alt="" draggable={false} onError={() => setImgFailed(true)} />
              {!done && !failed && <div className="clsc-beam" />}
            </div>
          </div>
        )}

        <div className={`clsc-label${failed ? " clsc-label--err" : ""}${(live && !ended) ? " clsc-label--live" : ""}`}>
          {live && !ended && <span className="clsc-live-dot" />}{label}
        </div>
        <div className="clsc-sub" key={sub}>{sub}</div>
        {title && <div className="clsc-vidtitle" title={title}>{title}</div>}
      </div>

      <div className="clsc-foot">
        <div className="clsc-card">
          <div className="clsc-card-row">
            <div className="clsc-stat">
              <span className="clsc-stat-label">CLIPS</span>
              <span className="clsc-stat-val">
                {resultsCount > 0 ? resultsCount
                  : (done || failed || ended) ? "0"
                  : <span className="clsc-dots" aria-label="Waiting for the first clip"><i /><i /><i /></span>}
              </span>
            </div>
            <div className="clsc-stat clsc-stat--right">
              <span className="clsc-stat-label">{live ? "STATUS" : "PROGRESS"}</span>
              <span className={`clsc-stat-val${(live && !ended) ? " clsc-stat-val--live" : ""}`}>{live ? (ended ? "ENDED" : "LIVE") : `${pct}%`}</span>
            </div>
          </div>
          {!live && !failed && (
            <ol className="clsc-steps" aria-label="Progress">
              {STEPS.map((name, i) => (
                <li key={name} className={i < step ? "is-done" : i === step ? "is-active" : ""}>
                  <span className="clsc-step-dot">{i < step ? "✓" : i + 1}</span>
                  <span className="clsc-step-name">{name}</span>
                </li>
              ))}
            </ol>
          )}
          <div className="clsc-bar">
            {live
              ? (ended
                  ? <div className="clsc-bar-fill" style={{ width: "100%" }} />
                  : <div className="clsc-bar-fill clsc-bar-fill--indet" />)
              : <div className="clsc-bar-fill" style={{ width: `${Math.max(3, pct)}%` }} />}
          </div>
          {minutesLeft && !done && !failed && (
            <div className="clsc-eta">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5" /><path d="M9 2h6" /></svg>
              {minutesLeft}
            </div>
          )}
        </div>

        {liveEnded ? (
          <div className="clsc-live-actions">
            {resultsCount > 0 && <button className="clsc-cancel clsc-cancel--primary" onClick={onViewClips}>View clips →</button>}
            {outOfCredits && onUpgrade
              ? <button className="clsc-cancel" onClick={onUpgrade}>Buy credits to keep clipping</button>
              : <button className="clsc-cancel" onClick={onClose}>Close</button>}
          </div>
        ) : live ? (
          <div className="clsc-live-actions">
            {resultsCount > 0 && <button className="clsc-cancel clsc-cancel--primary" onClick={onViewClips}>View clips →</button>}
            <button className="clsc-cancel" onClick={onClose}>Keep clipping in background</button>
            <button className="clsc-link" onClick={() => confirmCancel("Stop auto-clipping this stream? Clips made so far stay in your Library.")}>Stop auto-clipping</button>
          </div>
        ) : done ? (
          <button className="clsc-cancel clsc-cancel--primary" onClick={onViewClips}>View clips →</button>
        ) : inFlow && !failed ? (
          resultsCount > 0 ? (
            // First clips land while the rest render: let them watch right away.
            <div className="clsc-live-actions">
              <button className="clsc-cancel clsc-cancel--primary" onClick={onViewClips}>
                Your first clip{resultsCount === 1 ? " is" : "s are"} ready →
              </button>
              <p className="clsc-inflow">The rest are almost ready.</p>
            </div>
          ) : (
            <p className="clsc-inflow">We&apos;ll text and email you the moment they&apos;re ready.</p>
          )
        ) : resultsCount > 0 && !failed ? (
          // Clips are landing while the job is still rendering — let impatient
          // users go watch them now; Cancel stays available alongside.
          <div className="clsc-live-actions">
            <button className="clsc-cancel clsc-cancel--primary" onClick={onViewClips}>View {resultsCount} clip{resultsCount === 1 ? "" : "s"} →</button>
            {onCancel && <button className="clsc-link" onClick={() => confirmCancel("Stop clipping this video? Clips already made stay in your Library.")}>Cancel clipping</button>}
          </div>
        ) : (
          failed ? (
            inFlow && onRetry
              ? <button className="clsc-cancel clsc-cancel--primary" onClick={onRetry}>Try another stream →</button>
              : <button className="clsc-cancel" onClick={onCancel || onClose}>Close</button>
          ) : (
            <div className="clsc-live-actions">
              <button className="clsc-cancel clsc-cancel--primary" onClick={onClose}>Keep clipping in background</button>
              <button className="clsc-link" onClick={() => confirmCancel("Stop clipping this video? No clips will be made from it.")}>Cancel clipping</button>
            </div>
          )
        )}
      </div>
    </div>,
    document.body
  );
}
